import { createBrowserRouter } from 'react-router-dom'
import {
  SignInPage,
  SignUpPage,
  PasswordRecoveryPage,
  CreatePasswordPage,
  ConfirmEmailPage,
  SessionPage,
} from '@/pages/session'
import {
  HiringPage,
  MainPartnerPage,
  DocumentsPage,
  ProposalPage,
  PreContractPage,
  HiringCompanyPage,
  ContractPage,
} from '@/pages/hiring'
import {
  MonthlyDocumentsPage,
  MonthlyDocumentsUploadPage,
} from '@/pages/monthly-documents'
import { FiscalNotesPage, FiscalNoteIssuePage } from '@/pages/fiscal-notes'
import { HomePage } from '@/pages/home/home.page'
import { homeLoader } from '@/pages/home/router-loader'
import { DashboardPage } from '@/pages/dashboard/dashboard.page'
import { RevenuePage } from '@/pages/revenue/revenue.page'
import { TaxesPage } from '@/pages/taxes/taxes.page'
import { AccountPage } from '@/pages/account/account.page'
import { ErrorPage } from '@/pages/error/error.page'
import { AdditionalServicesPage } from '@/pages/additional-services/additional-services.page'
import { MenuPage } from '@/pages/menu/menu.page'

import { PrivateRoute } from '@/components'
import { DashboardLayout, BaseLayout } from '@/components/layouts'
import { ReferAndEarnPage } from '@/pages/refer-and-earn'
import { BillingPage } from '@/pages/billing/billing.page'
import { MaintenancePage } from './pages/maintenance/maintenance.page'
import { InvitePage } from '@/pages/invite/invite.page'
import { PartnersPage } from '@/pages/partners/partners.page'
import { PartnerPage } from '@/pages/partners/partner/partner.page'
import { PartnerDataPage } from '@/pages/partners/partner/partner-data/partner-data.page'
import { PartnerDependantsPage } from '@/pages/partners/partner/dependents/dependents.page'
import { CollaboratorsPage } from '@/pages/collaborators/collaborators.page'
import { InsurancePage } from '@/pages/insurance'
import { RcpQuotePage } from '@/pages/insurance/rcp-quote'

export const routes = {
  session: {
    signin: '/session/signin',
    signup: '/session/signup',
    passwordRecovery: '/session/password-recovery',
    createPassword: '/session/create-password',
    confirmEmail: '/session/confirm-email',
  },
  home: '/',
  dashboard: '/dashboard',
  account: '/account',
  revenue: '/revenue',
  fiscalNotes: {
    index: '/fiscal-notes',
    issue: '/fiscal-notes/issue',
  },
  taxes: '/taxes',
  monthlyDocuments: '/monthly-documents',
  monthlyDocumentsUpload: '/monthly-documents/:document_request_key/upload',
  additionalServices: '/additional-services',
  insurance: '/insurance',
  insuranceRcpQuote: '/insurance/rcp-medicos',
  referAndEarn: '/refer-and-earn',
  menu: '/menu',
  billing: '/billing',
  partners: {
    index: '/partners',
    data: '/partners/:partner_key/data',
    dependants: '/partners/:partner_key/dependants',
  },
  hiring: {
    index: '/hiring/:deal_key',
    proposal: '/hiring/:deal_key/proposal',
    preContract: '/hiring/:deal_key/pre-contract',
    mainPartner: '/hiring/:deal_key/main-partner',
    company: '/hiring/:deal_key/company',
    otherPartners: '/hiring/:deal_key/other-partners',
    documents: '/hiring/:deal_key/documents',
    contract: '/hiring/:deal_key/contract',
  },
  collaborators: '/collaborators',
}

export const maintenanceRouter = createBrowserRouter([
  {
    path: '*',
    element: <MaintenancePage />,
  },
])

export const router = createBrowserRouter([
  {
    element: <SessionPage />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/session/signin',
        index: true,
        element: <SignInPage />,
      },
      {
        path: '/session/signup',
        element: <SignUpPage />,
      },
      {
        path: '/session/password-recovery',
        element: <PasswordRecoveryPage />,
      },
      {
        path: '/session/new-password',
        element: <CreatePasswordPage isRecovery={true} />,
      },
      {
        element: <PrivateRoute />,
        children: [
          {
            path: '/session/create-password',
            element: <CreatePasswordPage />,
          },
          {
            path: '/session/confirm-email',
            element: <ConfirmEmailPage />,
          },
        ],
      },
    ],
  },
  {
    element: <PrivateRoute />,
    errorElement: <ErrorPage />,
    children: [
      {
        element: <BaseLayout />,
        children: [
          {
            path: '/',
            element: <HomePage />,
            loader: homeLoader,
          },
        ],
      },
      {
        path: routes.hiring.index,
        element: <HiringPage />,
        children: [
          {
            path: routes.hiring.proposal,
            element: <ProposalPage />,
          },
          {
            path: routes.hiring.preContract,
            element: <PreContractPage />,
          },
          {
            path: routes.hiring.mainPartner,
            element: <MainPartnerPage />,
          },
          {
            path: routes.hiring.company,
            element: <HiringCompanyPage />,
          },
          {
            path: routes.hiring.contract,
            element: <ContractPage />,
          },
          {
            path: routes.hiring.documents,
            element: <DocumentsPage />,
          },
        ],
      },
      {
        element: <DashboardLayout />,
        children: [
          {
            path: '/dashboard',
            element: <DashboardPage />,
          },
          {
            path: '/account',
            element: <AccountPage />,
          },
          {
            path: '/revenue',
            element: <RevenuePage />,
          },
          {
            path: '/fiscal-notes',
            element: <FiscalNotesPage />,
          },
          {
            path: '/fiscal-notes/issue',
            element: <FiscalNoteIssuePage />,
          },
          {
            path: '/taxes',
            element: <TaxesPage />,
          },
          {
            path: '/monthly-documents',
            element: <MonthlyDocumentsPage />,
          },
          {
            path: '/monthly-documents/:document_request_key/upload',
            element: <MonthlyDocumentsUploadPage />,
          },
          {
            path: '/additional-services',
            element: <AdditionalServicesPage />,
          },
          {
            path: '/insurance',
            element: <InsurancePage />,
          },
          {
            path: routes.insuranceRcpQuote,
            element: <RcpQuotePage />,
          },
          {
            path: '/refer-and-earn',
            element: <ReferAndEarnPage />,
          },
          {
            path: '/billing',
            element: <BillingPage />,
          },
          {
            path: '/menu',
            element: <MenuPage />,
          },
          {
            path: '/collaborators',
            element: <CollaboratorsPage />,
          },
          {
            path: '/partners',
            element: <PartnersPage />,
          },
          {
            path: '/partners/:partner_key',
            element: <PartnerPage />,
            children: [
              {
                path: '/partners/:partner_key/data',
                element: <PartnerDataPage />,
              },
              {
                path: '/partners/:partner_key/dependants',
                element: <PartnerDependantsPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/invite/accept/:deal_user_key',
    element: <InvitePage />,
  },
  {
    path: '*',
    element: <ErrorPage />,
  },
])
